import { useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, CircleAlert, CircleX, Loader2, Sparkles } from "lucide-react";
import { errMsg, useApi } from "@/lib/api";
import { analyzeSeo, textOf } from "./seoAnalysis";

const COLOR = { good: "text-emerald-400", ok: "text-amber-400", bad: "text-red-400" };
const ICON = { good: CheckCircle2, ok: CircleAlert, bad: CircleX };

function Score({ score }) {
  const color = score >= 75 ? "#10B981" : score >= 50 ? "#F59E0B" : "#EF4444";
  return (
    <div className="flex items-center gap-4" data-testid="seo-score">
      <div className="grid h-16 w-16 place-items-center rounded-full" style={{ background: `conic-gradient(${color} ${score * 3.6}deg, #23263A 0)` }}>
        <div className="grid h-12 w-12 place-items-center rounded-full bg-[#12141C] font-display text-lg font-bold text-white">{score}</div>
      </div>
      <div>
        <p className="font-semibold text-white">Punteggio SEO</p>
        <p className="text-xs" style={{ color }}>{score >= 75 ? "Ottimo, pronto per Google" : score >= 50 ? "Buono, si può migliorare" : "Da migliorare"}</p>
      </div>
    </div>
  );
}

function GooglePreview({ f }) {
  const title = f.seo_title || f.title || "Titolo dell'articolo";
  const meta = f.meta_description || f.excerpt || "La descrizione apparirà qui. Scrivine una che inviti al click.";
  const cut = (s, n) => (s.length > n ? s.slice(0, n - 1).trimEnd() + "…" : s);
  return (
    <div data-testid="google-preview">
      <p className="mb-2 text-xs uppercase tracking-widest text-slate-500">Anteprima su Google</p>
      <div className="rounded-xl bg-white p-4 font-[arial,sans-serif]">
        <div className="flex items-center gap-2">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-amber-500 text-xs font-bold text-black">PG</span>
          <div className="leading-tight">
            <p className="text-sm text-[#202124]">Club Prezzi</p>
            <p className="text-xs text-[#4d5156]">{window.location.host} › blog › {f.slug || "indirizzo-articolo"}</p>
          </div>
        </div>
        <p className="mt-2 text-xl leading-snug text-[#1a0dab]">{cut(title, 60)}</p>
        <p className="mt-1 text-sm leading-snug text-[#4d5156]">{cut(meta, 158)}</p>
      </div>
    </div>
  );
}

function AiSuggest({ f, setForm }) {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const [s, setS] = useState(null);
  const run = async () => {
    const { text } = textOf(f.content);
    if (text.length < 20) return toast.error("Scrivi prima almeno qualche riga di testo");
    setBusy(true);
    try {
      setS(await api("post", "/admin/blog/seo-suggest", { title: f.title, text, focus_keyword: f.focus_keyword }));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const FIELDS = [["focus_keyword", "Parola chiave"], ["seo_title", "Titolo SEO"], ["meta_description", "Meta description"], ["slug", "Slug"], ["excerpt", "Riassunto"]];
  return (
    <div data-testid="ai-suggest">
      <button type="button" data-testid="ai-suggest-btn" onClick={run} disabled={busy}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-amber-500/40 px-4 py-2.5 text-sm font-semibold text-amber-300 transition-colors hover:bg-amber-500/10 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} {busy ? "L'AI sta analizzando…" : "Suggerisci con AI"}
      </button>
      {s && (
        <div className="mt-3 space-y-2 rounded-xl border border-white/10 bg-[#0B0C10] p-3 text-sm">
          {FIELDS.filter(([k]) => s[k]).map(([k, label]) => (
            <div key={k} className="flex items-start justify-between gap-3">
              <div><p className="text-xs text-slate-500">{label}</p><p className="text-slate-200">{s[k]}</p></div>
              <button type="button" data-testid={`ai-apply-${k}`} onClick={() => setForm((p) => ({ ...p, [k]: s[k] }))} className="shrink-0 text-xs font-semibold text-amber-400 hover:underline">Applica</button>
            </div>
          ))}
          <button type="button" data-testid="ai-apply-all" onClick={() => setForm((p) => ({ ...p, ...Object.fromEntries(FIELDS.filter(([k]) => s[k]).map(([k]) => [k, s[k]])) }))}
            className="w-full rounded-lg bg-amber-500 py-2 text-xs font-bold text-black hover:bg-amber-400">Applica tutti i suggerimenti</button>
          {!!s.tips?.length && (
            <ul className="list-disc space-y-1 pl-5 pt-1 text-xs text-slate-400">{s.tips.map((t) => <li key={t}>{t}</li>)}</ul>
          )}
        </div>
      )}
    </div>
  );
}

export function SeoPanel({ form, setForm }) {
  const { checks, score, words } = useMemo(() => analyzeSeo(form), [form]);
  const order = { bad: 0, ok: 1, good: 2 };
  return (
    <div className="space-y-5 rounded-2xl border border-white/10 bg-[#12141C] p-5" data-testid="seo-panel">
      <div className="flex items-center justify-between"><Score score={score} /><p className="text-xs text-slate-500">{words} parole</p></div>
      <GooglePreview f={form} />
      <AiSuggest f={form} setForm={setForm} />
      <div>
        <p className="mb-2 text-xs uppercase tracking-widest text-slate-500">Analisi e consigli</p>
        <ul className="space-y-2" data-testid="seo-checks">
          {[...checks].sort((a, b) => order[a[0]] - order[b[0]]).map(([st, msg]) => {
            const I = ICON[st];
            return <li key={msg} className="flex gap-2 text-sm text-slate-300"><I className={`mt-0.5 h-4 w-4 shrink-0 ${COLOR[st]}`} />{msg}</li>;
          })}
        </ul>
      </div>
    </div>
  );
}
